import React, { useEffect, useState, Fragment } from 'react';
import { Phone, Menu, X } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollTo = (id: string) => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const navLinks = [{
    name: t('nav.home'),
    id: 'home'
  }, {
    name: t('nav.services'),
    id: 'services'
  }, {
    name: t('nav.about'),
    id: 'about'
  }, {
    name: t('nav.reviews'),
    id: 'reviews'
  }, {
    name: t('nav.contact'),
    id: 'contact'
  }];
  // Brand wordmark color: navy when scrolled (over white), white when transparent (over hero)
  const wordmarkColor = isScrolled ? 'text-brand-navy' : 'text-white';
  const linkBase = isScrolled ? 'text-brand-navy' : 'text-white';
  // Shorter CTA label in Spanish to prevent wrapping
  const quoteLabel = lang === 'es' ? 'COTIZACIÓN' : t('nav.quote');
  return <nav className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 h-[72px] ${isScrolled ? 'bg-white backdrop-blur-[12px] shadow-[0_2px_20px_rgba(0,0,0,0.08)] border-b border-brand-gold/30' : 'bg-transparent'}`} data-id="element-162">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center flex-nowrap gap-2 overflow-visible" data-id="element-163">
        {/* Left: Logo */}
        <div className="flex items-center gap-3 cursor-pointer flex-shrink-0 mr-4 lg:mr-8" onClick={() => scrollTo('home')} data-id="element-164">
          <div className={`flex flex-col justify-center ${isScrolled ? '' : 'px-3 py-1.5 border border-brand-gold/60 rounded'}`} data-id="element-165">
            <span className={`font-script font-bold text-[22px] lg:text-[24px] leading-none whitespace-nowrap ${wordmarkColor}`} data-id="element-166">
              TwoGirls <span className="text-brand-gold" data-id="element-167">♡</span> MopCo
            </span>
            <span className="text-brand-gold font-montserrat font-medium text-[9px] tracking-[3px] leading-none mt-1 whitespace-nowrap" data-id="element-168">
              {t('brand.tagline')}
            </span>
          </div>
        </div>

        {/* Center: Links (Desktop) */}
        <div className="hidden lg:flex items-center gap-4 xl:gap-6 flex-shrink-0" data-id="element-169">
          {navLinks.map((link, index) => <Fragment key={link.id} data-id="element-170">
              <button onClick={() => scrollTo(link.id)} className={`${linkBase} hover:text-brand-gold transition-colors font-montserrat font-medium text-[13px] tracking-wide whitespace-nowrap flex-shrink-0`} data-id="element-171">
                {link.name}
              </button>
              {index < navLinks.length - 1 && <span className={isScrolled ? 'text-brand-gold/40' : 'text-white/30'} data-id="element-172">
                  ·
                </span>}
            </Fragment>)}
        </div>

        {/* Right: Actions (Desktop) */}
        <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-shrink-0 ml-auto" data-id="element-173">
          <a href="tel:7866603720" className={`flex items-center gap-1.5 border-[1.5px] border-brand-gold rounded-full px-3 py-1.5 ${isScrolled ? 'text-brand-navy' : 'text-white'} text-[12px] font-medium hover:bg-brand-gold hover:text-white transition-colors whitespace-nowrap flex-shrink-0`} data-id="element-174">
            <Phone size={14} data-id="element-175" />
            (786) 660-3720
          </a>

          <div className={`flex items-center rounded-full p-0.5 border flex-shrink-0 ${isScrolled ? 'border-brand-navy/30 bg-brand-navy/5' : 'border-white/30 bg-black/20'}`} data-id="element-176">
            <button onClick={() => setLang('en')} className={`px-2 py-1 rounded-[20px] text-[11px] transition-all whitespace-nowrap ${lang === 'en' ? 'bg-brand-gold text-brand-navy font-bold' : `${isScrolled ? 'text-brand-navy/60' : 'text-white/65'} hover:text-brand-gold`}`} data-id="element-177">
              🇺🇸 EN
            </button>
            <button onClick={() => setLang('es')} className={`px-2 py-1 rounded-[20px] text-[11px] transition-all whitespace-nowrap ${lang === 'es' ? 'bg-brand-gold text-brand-navy font-bold' : `${isScrolled ? 'text-brand-navy/60' : 'text-white/65'} hover:text-brand-gold`}`} data-id="element-178">
              🇪🇸 ES
            </button>
          </div>

          <button onClick={() => scrollTo('contact')} className="bg-brand-navy hover:bg-brand-gold text-white hover:text-brand-navy font-montserrat font-extrabold px-3.5 py-2 rounded-[25px] text-[11px] transition-colors whitespace-nowrap flex-shrink-0" data-id="element-179">
            {quoteLabel}
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="lg:hidden flex items-center gap-4 ml-auto" data-id="element-180">
          <div className={`flex items-center rounded-full p-1 border ${isScrolled ? 'border-brand-navy/30' : 'border-white/30 bg-black/20'}`} data-id="element-181">
            <button onClick={() => setLang('en')} className={`px-2 py-1 rounded-[20px] text-xs transition-all ${lang === 'en' ? 'bg-brand-gold text-brand-navy font-bold' : isScrolled ? 'text-brand-navy/60' : 'text-white/65'}`} data-id="element-182">
              EN
            </button>
            <button onClick={() => setLang('es')} className={`px-2 py-1 rounded-[20px] text-xs transition-all ${lang === 'es' ? 'bg-brand-gold text-brand-navy font-bold' : isScrolled ? 'text-brand-navy/60' : 'text-white/65'}`} data-id="element-183">
              ES
            </button>
          </div>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`${isScrolled ? 'text-brand-navy' : 'text-white'} p-2`} aria-label="Toggle menu" data-id="element-184">
            {isMobileMenuOpen ? <X size={24} data-id="element-185" /> : <Menu size={24} data-id="element-186" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Accordion/Drawer */}
      {isMobileMenuOpen && <div className="lg:hidden absolute top-[72px] left-0 w-full bg-brand-navy border-t border-brand-gold/30 shadow-xl" data-id="element-187">
          <div className="flex flex-col p-4 space-y-4" data-id="element-188">
            {navLinks.map(link => <button key={link.id} onClick={() => scrollTo(link.id)} className="text-white text-left text-lg font-montserrat font-medium py-2 border-b border-brand-gold/20 hover:text-brand-gold transition-colors" data-id="element-189">
                {link.name}
              </button>)}
            <a href="tel:7866603720" className="flex items-center justify-center gap-2 border border-brand-gold rounded-full px-4 py-3 text-white font-medium mt-4" data-id="element-190">
              <Phone size={18} data-id="element-191" />
              (786) 660-3720
            </a>
            <button onClick={() => scrollTo('contact')} className="bg-brand-gold text-brand-navy font-montserrat font-extrabold px-6 py-3 rounded-[25px] w-full mt-2" data-id="element-192">
              {t('nav.quote')}
            </button>
          </div>
        </div>}
    </nav>;
};